import React, { useCallback, useEffect, useRef, useState } from 'react';
import FuriganaText from './FuriganaText';

/**
 * ImageCropStep — optional, mobile-friendly client-side crop.
 *
 * Privacy control (P0-3, Layer 1): lets a student draw a rectangle over the
 * region to KEEP (graph + problem + their own work) so a name written in a
 * header/margin can be cropped OFF before the image is uploaded to the vision
 * APIs. The crop is entirely user-controlled, so the content the AI needs is
 * preserved by construction.
 *
 * Self-contained (no new npm dependency): the selection is drawn with pointer
 * and touch events on a transparent overlay, and the crop is produced by
 * blitting the chosen source rectangle onto an offscreen <canvas> and calling
 * canvas.toBlob(). Displayed (CSS-scaled) coordinates are mapped back to the
 * image's natural pixels before the blit so the crop is pixel-accurate on both
 * desktop and phone.
 *
 * Props:
 *   file       — the original File (used for name + output MIME type)
 *   imageSrc   — data URL / object URL shown to the user (the existing preview)
 *   onCropped  — (croppedFile: File) => void, called when the user applies
 *   onSkip     — () => void, called for "use whole image" (keep the original)
 *   t          — i18next translation function (passed from GraphUploader)
 */

// Ignore accidental taps: a selection smaller than this (in displayed px) is
// treated as "no selection" so the Apply button stays disabled.
const MIN_SELECTION_PX = 6;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function ImageCropStep({ file, imageSrc, onCropped, onSkip, t }) {
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const dragStartRef = useRef(null);
  // Selection in DISPLAYED (CSS) pixels, normalized to a positive-area rect.
  const [selection, setSelection] = useState(null);

  // Map a viewport (clientX/clientY) point to a point inside the overlay, which
  // is inset-0 over the image so its box == the image's displayed box.
  const pointFromClient = useCallback((clientX, clientY) => {
    const el = overlayRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const width = rect.width || el.clientWidth || 0;
    const height = rect.height || el.clientHeight || 0;
    // When the box has no measured size (e.g. jsdom), don't clamp to zero.
    const maxX = width > 0 ? width : Number.POSITIVE_INFINITY;
    const maxY = height > 0 ? height : Number.POSITIVE_INFINITY;
    return {
      x: clamp(clientX - rect.left, 0, maxX),
      y: clamp(clientY - rect.top, 0, maxY),
    };
  }, []);

  const beginSelection = useCallback(
    (clientX, clientY) => {
      const p = pointFromClient(clientX, clientY);
      dragStartRef.current = p;
      setSelection({ x: p.x, y: p.y, w: 0, h: 0 });
    },
    [pointFromClient]
  );

  const updateSelection = useCallback(
    (clientX, clientY) => {
      const start = dragStartRef.current;
      if (!start) return;
      const p = pointFromClient(clientX, clientY);
      setSelection({
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
      });
    },
    [pointFromClient]
  );

  const endSelection = useCallback(() => {
    dragStartRef.current = null;
  }, []);

  // Mouse: start on the surface, then track/release on window so a fast drag
  // that leaves the image still updates and always ends cleanly.
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      beginSelection(e.clientX, e.clientY);
      const onMove = (ev) => updateSelection(ev.clientX, ev.clientY);
      const onUp = () => {
        endSelection();
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [beginSelection, updateSelection, endSelection]
  );

  // Touch: attach NATIVE non-passive listeners so preventDefault() on touchmove
  // actually stops the page from scrolling during a drag (React's synthetic
  // touch listeners are registered passive, where preventDefault is a no-op).
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const touch = e.touches[0];
      beginSelection(touch.clientX, touch.clientY);
    };
    const onTouchMove = (e) => {
      if (!dragStartRef.current) return;
      e.preventDefault();
      const touch = e.touches && e.touches[0];
      if (touch) updateSelection(touch.clientX, touch.clientY);
    };
    const onTouchEnd = () => endSelection();

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [beginSelection, updateSelection, endSelection]);

  const hasSelection =
    !!selection && selection.w >= MIN_SELECTION_PX && selection.h >= MIN_SELECTION_PX;

  const clearSelection = useCallback(() => {
    dragStartRef.current = null;
    setSelection(null);
  }, []);

  const handleApply = useCallback(() => {
    const img = imgRef.current;
    if (!img || !selection || !hasSelection) return;

    // Displayed size (fallback to the selection extent when unmeasured, e.g.
    // jsdom) → natural pixels. scale === 1 in the fallback case.
    const displayW = img.clientWidth || img.width || selection.w;
    const displayH = img.clientHeight || img.height || selection.h;
    const naturalW = img.naturalWidth || displayW;
    const naturalH = img.naturalHeight || displayH;
    const scaleX = displayW > 0 ? naturalW / displayW : 1;
    const scaleY = displayH > 0 ? naturalH / displayH : 1;

    const sx = Math.round(selection.x * scaleX);
    const sy = Math.round(selection.y * scaleY);
    const sw = Math.max(1, Math.round(selection.w * scaleX));
    const sh = Math.max(1, Math.round(selection.h * scaleY));

    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Canvas unavailable — degrade to keeping the whole image rather than
      // blocking the upload.
      onSkip();
      return;
    }
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

    const outputType =
      file && typeof file.type === 'string' && file.type.startsWith('image/')
        ? file.type
        : 'image/png';
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          onSkip();
          return;
        }
        const name = (file && file.name) || 'cropped.png';
        const croppedFile = new File([blob], name, { type: blob.type || outputType });
        onCropped(croppedFile);
      },
      outputType,
      0.92
    );
  }, [selection, hasSelection, file, onCropped, onSkip]);

  return (
    <div
      className="mt-6 rounded-[28px] p-6 shadow-inner"
      style={{ border: '1px solid var(--border)', backgroundColor: 'var(--warm-bg)' }}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-2xl leading-none">
          🛡️
        </span>
        <div>
          <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
            <FuriganaText text={t('upload.cropPromptTitle')} />
          </h3>
          <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('upload.cropPromptBody')} />
          </p>
        </div>
      </div>

      <div className="mt-5 flex justify-center">
        <div className="relative inline-block max-w-full">
          <img
            ref={imgRef}
            src={imageSrc}
            alt={t('upload.previewAlt')}
            draggable={false}
            className="block max-h-[60vh] max-w-full rounded-2xl"
            style={{ border: '1px solid var(--border)', touchAction: 'none' }}
          />
          <div
            ref={overlayRef}
            role="application"
            aria-label={t('upload.cropHint')}
            onMouseDown={handleMouseDown}
            className="absolute inset-0 cursor-crosshair overflow-hidden rounded-2xl"
            style={{ touchAction: 'none' }}
          >
            {hasSelection && (
              <div
                className="absolute"
                style={{
                  left: `${selection.x}px`,
                  top: `${selection.y}px`,
                  width: `${selection.w}px`,
                  height: `${selection.h}px`,
                  border: '2px solid var(--primary)',
                  borderRadius: '4px',
                  // Darken everything OUTSIDE the selection (the overlay clips
                  // the huge spread), spotlighting the region to keep.
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
                }}
              />
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
        <FuriganaText text={t('upload.cropHint')} />
      </p>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={handleApply}
          disabled={!hasSelection}
          className="btn-primary rounded-full px-6 py-3 text-sm font-semibold shadow-md transition-transform hover:scale-[1.01] disabled:scale-100 disabled:opacity-60"
        >
          <FuriganaText text={t('upload.cropApply')} />
        </button>
        {hasSelection && (
          <button
            type="button"
            onClick={clearSelection}
            className="btn-secondary rounded-full px-6 py-3 text-sm font-semibold"
          >
            <FuriganaText text={t('upload.cropRedo')} />
          </button>
        )}
        <button
          type="button"
          onClick={onSkip}
          className="btn-secondary rounded-full px-6 py-3 text-sm font-semibold"
        >
          <FuriganaText text={t('upload.cropSkip')} />
        </button>
      </div>
    </div>
  );
}

export default ImageCropStep;
